/* eslint-disable @atlaskit/design-system/prefer-primitives */
/* eslint-disable @repo/internal/dom-events/no-unsafe-event-listeners */
/* @jsx jsx */
/** @jsxfrag */
import { jsx } from '@emotion/react';
import debounce from 'lodash/debounce';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import { messages } from './messages';
import { ListHeading } from './ListHeading';
import { ListItem } from './ListItem';
import { suggestionsContainerStyles, suggestionsHeadingStyles, suggestionsScrollerStyles } from './styles';
export const Suggestions = ({
  suggestionsHeading,
  suggestions,
  value,
  setSuggestion
}) => {
  const {
    formatMessage
  } = useIntl();

  /**
   * While scrolling with the mouse, the setSuggestion() function can still be
   * called by the mousemove event in ListItem.
   *
   * So while the scrolling is happening, another scrollTo() is started and
   * conflicts with the current scrolling momentum, causing the experience to
   * jitter and feel clunky.
   *
   * While scrolling with just the mouse, this is the order of events:
   * - (multiple) scroll
   * - mousemove
   * - (multiple) scroll
   * - mousemove
   * - scrollend
   *
   * However, if the user leaves the cursor over the list while navigating with
   * the keyboard and it scrolls to ensure items are visible, the order of
   * events is:
   * - (multiple) on scroll
   * - scrollend
   * - mousemove
   *
   * So to avoid the mousemove from triggering a change, we set a flag to
   * ignore mousemove events until a bit after its over.
   */
  const ignoreMouseMoveRef = React.useRef(false);
  const scrollerRef = React.useRef(null);
  const smoothSetSuggestion = React.useCallback((suggestion, eventName) => {
    if (!setSuggestion) {
      return;
    }

    // Ignore mousemove
    if (eventName === 'mousemove' && ignoreMouseMoveRef.current) {
      return;
    }
    setSuggestion(suggestion);
  }, [setSuggestion]);
  React.useEffect(() => {
    if (!scrollerRef.current) {
      return;
    }

    // Debounced handler to restore tracking after scrolling
    const allowMouseTracking = debounce(() => {
      ignoreMouseMoveRef.current = false;
    }, 75);
    const onScrollEvent = () => {
      ignoreMouseMoveRef.current = true;
      allowMouseTracking();
    };
    const scroller = scrollerRef.current;
    scroller.addEventListener('scroll', onScrollEvent);
    scroller.addEventListener('scrollend', onScrollEvent);
    return () => {
      scroller.removeEventListener('scroll', onScrollEvent);
      scroller.removeEventListener('scrollend', onScrollEvent);
    };
  }, [scrollerRef]);
  const renderedGroupSuggestions = useMemo(() => {
    return (suggestions || []).reduce((updatedSuggestions, suggestion, index, originalArray) => {
      const previousRecord = originalArray[index - 1];

      // @TODO EDF-840: Improve HTML structure with suggestion grouping for a11y
      // We will need to put children under a nested <ul> but this also
      // comes with all the implication of extra testing and updating tests
      if (suggestion.group && previousRecord && previousRecord.group !== suggestion.group) {
        updatedSuggestions.push(jsx(ListHeading, {
          key: `group_${suggestion.group}`,
          heading: suggestion.group
        }));
      }
      updatedSuggestions.push(jsx(ListItem, {
        key: suggestion.value,
        suggestion: suggestion,
        customLabel: suggestion.value === 'tell-ai' ? formatMessage(messages.tellAILabel) : undefined,
        highlighted: suggestion.value === value,
        setSuggestion: smoothSetSuggestion
      }));
      return updatedSuggestions;
    }, []);
  }, [formatMessage, smoothSetSuggestion, suggestions, value]);
  if (!renderedGroupSuggestions.length) {
    return null;
  }
  return jsx("div", {
    "data-testid": "suggestions-container",
    css: suggestionsScrollerStyles,
    ref: scrollerRef
  }, suggestionsHeading && jsx("h2", {
    css: suggestionsHeadingStyles
  }, suggestionsHeading), jsx("ul", {
    "data-testid": "editor-plugin-ai-suggestion-list",
    id: "suggestion-list",
    css: suggestionsContainerStyles
  }, renderedGroupSuggestions));
};